import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshStandardMaterial, SphereGeometry } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import type { VehicleSpec } from './types';

const UNIT = new BoxGeometry(1, 1, 1);

function block(color: string, w: number, h: number, d: number, x: number, y: number, z: number): Mesh {
  const mesh = new Mesh(UNIT, flatMaterial(color));
  mesh.scale.set(w, h, d);
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  return mesh;
}

function createMate(): { root: Group; arm: Group } {
  const root = new Group();
  root.add(block('#1d7874', 0.4, 0.6, 0.28, 0, 1.2, 0), block('#22223b', 0.34, 0.68, 0.26, 0, 0.58, 0));
  const head = new Mesh(new SphereGeometry(0.15, 10, 8), flatMaterial('#4a2c1d'));
  head.position.y = 1.66;
  const arm = new Group();
  arm.position.set(0.08, 1.45, 0);
  const forearm = new Mesh(new CylinderGeometry(0.05, 0.05, 0.62), flatMaterial('#4a2c1d'));
  forearm.position.y = 0.31;
  arm.add(forearm);
  root.add(head, arm);
  return { root, arm };
}

/**
 * The sliding door and the mate. Closed and hidden while driving; at a stop the door
 * slides back, and the mate leans out calling the next destination.
 */
export class DoorRig {
  readonly group = new Group();
  private readonly doorway: Mesh;
  private readonly panel: Mesh;
  private readonly mate = createMate();
  private readonly doorLength: number;
  private readonly doorCentreZ: number;
  private readonly sideX: number;
  private open = 0;
  private clock = 0;

  constructor(spec: VehicleSpec, bodyColor: string, withMate = true) {
    const door = spec.door ?? { u0: spec.length * 0.4, u1: spec.length * 0.6, v0: 0.4, v1: spec.height * 0.85 };
    this.doorLength = door.u1 - door.u0;
    this.doorCentreZ = spec.length / 2 - (door.u0 + door.u1) / 2;
    this.sideX = spec.width / 2;
    const height = door.v1 - door.v0;
    this.doorway = block('#0b0f13', 0.02, height, this.doorLength, this.sideX + 0.015, door.v0 + height / 2, this.doorCentreZ);
    this.panel = new Mesh(UNIT, new MeshStandardMaterial({ color: bodyColor }));
    this.panel.scale.set(0.05, height, this.doorLength);
    this.panel.position.set(this.sideX + 0.06, door.v0 + height / 2, this.doorCentreZ);
    this.mate.root.position.set(this.sideX - 0.3, 0.1, this.doorCentreZ - this.doorLength * 0.2);
    this.group.add(this.doorway, this.panel);
    if (withMate) this.group.add(this.mate.root);
    this.apply();
  }

  /** wantOpen: at a stop with the bus standing still. Opens slowly, shuts fast when you pull off. */
  update(wantOpen: boolean, dt: number): void {
    this.clock += dt;
    const target = wantOpen ? 1 : 0;
    const rate = wantOpen ? 2.2 : 4;
    this.open += Math.sign(target - this.open) * Math.min(Math.abs(target - this.open), rate * dt);
    this.apply();
  }

  private apply(): void {
    const o = this.open;
    this.doorway.visible = o > 0.02;
    this.doorway.scale.z = Math.max(0.01, this.doorLength * o);
    this.doorway.position.z = this.doorCentreZ - (this.doorLength * (1 - o)) / 2;
    this.panel.visible = o > 0.02;
    this.panel.position.z = this.doorCentreZ + this.doorLength * o * 0.95;
    // The mate stays inside until the door is mostly open, then leans out.
    const out = Math.max(0, (o - 0.5) / 0.5);
    this.mate.root.visible = out > 0;
    this.mate.root.position.x = this.sideX - 0.3 + out * 0.55;
    this.mate.root.rotation.z = -0.18 * out;
    this.mate.arm.rotation.z = -0.4 - Math.max(0, Math.sin(this.clock * 5)) * 0.9 * out;
  }
}
